import { defineComponent, ref, computed, watch, nextTick } from "vue";
import { useI18n } from "../../../locales/index.mjs";
import Input from "../../common/base/Input/index.mjs";
import Select from "../../common/base/Select/index.mjs";
import OPtion from "../../common/base/Option/index.mjs";
import Checkbox from "../../common/base/Checkbox/index.mjs";
import TuiButton from "../../common/base/Button.vue.mjs";
import Avatar from "../../common/Avatar.vue.mjs";
import Datepicker from "../../common/base/Datepicker/index.mjs";
import Timepicker from "../../common/base/Timepicker/index.mjs";
import TuiDurationTimePicker from "../DurationTimePicker.vue.mjs";
import TimezonePicker from "../TimezonePicker.vue.mjs";
import TuiShareLink from "../ShareLink.vue.mjs";
import Contacts from "../Contacts.vue.mjs";
import ScheduleAttendees from "../../common/icons/ScheduleAttendees.vue.mjs";
import PanelContainer from "../PanelContainer.vue.mjs";
import CloseIcon from "../../common/icons/CloseIcon.vue.mjs";
import HideContentIcon from "../../common/icons/HideContentIcon.vue.mjs";
import ShowContentIcon from "../../common/icons/ShowContentIcon.vue.mjs";
import "../../../services/main.mjs";
import { roomService } from "../../../services/roomService.mjs";
import { EventType } from "../../../services/types.mjs";
import { TUISeatMode, TUIConferenceStatus } from "@tencentcloud/tuiroom-engine-js";
import "../../../utils/environment.mjs";
import { deepClone, calculateByteLength } from "../../../utils/utils.mjs";
import "mitt";
import "../../../services/manager/roomActionManager.mjs";
import "@tencentcloud/tui-core";
import { PASSWORD_MAX_LENGTH_LIMIT } from "../../../constants/room.mjs";
import { invalidDigitalPasswordRegex } from "../../../utils/common.mjs";
import { getDateAndTime, convertToTimestamp, calculateEndTime } from "../scheduleUtils.mjs";
import SvgIcon from "../../common/base/SvgIcon.vue.mjs";
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "ScheduleConferencePanelPC",
  props: {
    userName: null,
    visible: { type: Boolean },
    conferenceInfo: null
  },
  emits: ["input"],
  setup(__props, { emit }) {
    const props = __props;
    const { t } = useI18n();
    const shareLinkData = ref({});
    const isDialogVisible = ref(false);
    const contactsVisible = ref(false);
    const showRoomInvite = ref(false);
    const isShowPassword = ref(false);
    const passwordChecked = ref(false);
    const isEditMode = computed(() => !!props.conferenceInfo);
    const roomId = ref("");
    const contacts = ref([]);
    const { date: startDate, laterTime: startTime } = getDateAndTime(/* @__PURE__ */ new Date());
    const defaultFormData = ref({
      roomName: t("sb temporary room", {
        name: props.userName || roomService.basicStore.userId
      }),
      roomMode: "FreeToSpeak",
      startDate,
      startTime,
      duration: 1800,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      searchUser: "",
      scheduleAttendees: [],
      isMicrophoneDisableForAllUser: false,
      isScreenShareDisableForAllUser: false,
      isCameraDisableForAllUser: false,
      password: ""
    });
    const form = ref(deepClone(defaultFormData.value));
    const inputType = computed(() => isShowPassword.value ? "text" : "password");
    const toggleShowPassword = () => {
      isShowPassword.value = !isShowPassword.value;
    };
    const resetData = () => {
      defaultFormData.value.startDate = form.value.startDate;
      defaultFormData.value.startTime = form.value.startTime;
      form.value = Object.assign({}, deepClone(defaultFormData.value));
    };
    watch(
      () => props.visible,
      async (val) => {
        isDialogVisible.value = val;
        if (val) {
          const { date, laterTime } = getDateAndTime(/* @__PURE__ */ new Date());
          form.value.roomName = t("sb temporary room", {
            name: props.userName || roomService.basicStore.userId
          });
          form.value.startDate = date;
          form.value.startTime = laterTime;
          contacts.value = await roomService.scheduleConferenceManager.fetchFriendList();
          isEditMode.value && (form.value = Object.assign({}, deepClone(editParams.value)));
        }
      },
      {
        immediate: true
      }
    );
    const updateDialogVisible = (val) => {
      emit("input", val);
      if (!val) {
        passwordChecked.value = false;
        isShowPassword.value = false;
        resetData();
      }
    };
    watch(
      isDialogVisible,
      (val) => {
        updateDialogVisible(val);
      },
      { immediate: true }
    );
    const editParams = computed(() => {
      if (!props.conferenceInfo) return {};
      const {
        basicRoomInfo,
        scheduleAttendees,
        scheduleStartTime,
        scheduleEndTime
      } = props.conferenceInfo;
      const { date, time } = getDateAndTime(new Date(scheduleStartTime * 1e3));
      const {
        roomName,
        isSeatEnabled,
        isMicrophoneDisableForAllUser,
        isScreenShareDisableForAllUser,
        isCameraDisableForAllUser,
        password
      } = basicRoomInfo;
      return {
        roomName,
        roomMode: isSeatEnabled ? "SpeakAfterTakingSeat" : "FreeToSpeak",
        startDate: date,
        startTime: time,
        duration: scheduleEndTime - scheduleStartTime,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        searchUser: "",
        scheduleAttendees,
        isMicrophoneDisableForAllUser,
        isScreenShareDisableForAllUser,
        isCameraDisableForAllUser,
        password
      };
    });
    const scheduleParams = computed(() => {
      const { startDate: startDate2, startTime: startTime2, timezone, duration, scheduleAttendees } = form.value;
      const scheduleStartTime = convertToTimestamp(startDate2, startTime2, timezone);
      const scheduleEndTime = calculateEndTime(scheduleStartTime, duration);
      return {
        roomId: roomId.value,
        scheduleStartTime: scheduleStartTime / 1e3,
        scheduleEndTime: scheduleEndTime / 1e3,
        scheduleAttendees,
        roomName: form.value.roomName,
        isSeatEnabled: form.value.roomMode !== "FreeToSpeak",
        seatMode: form.value.roomMode === "SpeakAfterTakingSeat" ? TUISeatMode.kApplyToTake : void 0,
        isMicrophoneDisableForAllUser: form.value.isMicrophoneDisableForAllUser,
        isScreenShareDisableForAllUser: form.value.isScreenShareDisableForAllUser,
        isCameraDisableForAllUser: form.value.isCameraDisableForAllUser,
        password: form.value.password
      };
    });
    const roomPasswordCheck = () => {
      if (!passwordChecked.value) {
        form.value.password = "";
        return true;
      }
      const { password } = form.value;
      if (calculateByteLength(password) !== PASSWORD_MAX_LENGTH_LIMIT) {
        roomService.emit(EventType.ROOM_NOTICE_MESSAGE, {
          type: "warning",
          message: t("Your room password format is incorrect, please check it")
        });
        return false;
      }
      return true;
    };
    const timeCheck = () => {
      if (!props.visible) return true;
      const { timezone, startDate: startDate2, startTime: startTime2 } = form.value;
      const scheduleStartTime = convertToTimestamp(startDate2, startTime2, timezone);
      const currentDate = /* @__PURE__ */ new Date();
      currentDate.setMilliseconds(0);
      currentDate.setSeconds(0);
      const currentTime = currentDate.getTime();
      const result = scheduleStartTime >= currentTime;
      !result && roomService.emit(EventType.ROOM_NOTICE_MESSAGE, {
        type: "warning",
        message: t("The start time cannot be earlier than the current time")
      });
      return result;
    };
    const roomNameCheck = () => {
      if (!props.visible) return true;
      if (form.value.roomName === "") {
        roomService.emit(EventType.ROOM_NOTICE_MESSAGE, {
          type: "warning",
          message: t("The room name cannot be empty")
        });
        return false;
      }
      const result = calculateByteLength(form.value.roomName) <= 100;
      !result && roomService.emit(EventType.ROOM_NOTICE_MESSAGE, {
        type: "warning",
        message: t("The room name cannot exceed 100 characters")
      });
      return result;
    };
    const roomStatusCheck = () => {
      var _a;
      if (!props.visible) return true;
      const isNotStarted = ((_a = props.conferenceInfo) == null ? void 0 : _a.status) === TUIConferenceStatus.kConferenceStatusNotStarted;
      !isNotStarted && roomService.emit(EventType.ROOM_NOTICE_MESSAGE, {
        type: "warning",
        message: t(
          "The meeting is in progress and any meeting information cannot be modified"
        )
      });
      return isNotStarted;
    };
    watch(
      () => form.value.startTime,
      async (newValue, oldValue) => {
        if (!timeCheck()) {
          await nextTick();
          form.value.startTime = oldValue;
        }
      }
    );
    watch(
      () => form.value.startDate,
      async (newValue, oldValue) => {
        if (!timeCheck()) {
          await nextTick();
          form.value.startDate = oldValue;
        }
      }
    );
    watch(
      () => form.value.password,
      async (val) => {
        if (val && invalidDigitalPasswordRegex.test(val)) {
          await nextTick();
          form.value.password = val.replace(invalidDigitalPasswordRegex, "");
        }
      }
    );
    watch(
      () => passwordChecked.value,
      (val) => {
        if (val) {
          form.value.password = `${Math.floor(Math.random() * 9e5) + 1e5}`;
        }
      }
    );
    watch(
      () => form.value.timezone,
      async (newValue, oldValue) => {
        const { startDate: startDate2, startTime: startTime2 } = form.value;
        const currentDate = new Date(
          convertToTimestamp(startDate2, startTime2, newValue, -1, oldValue)
        );
        const { date, laterTime } = getDateAndTime(currentDate);
        form.value.startDate = date;
        form.value.startTime = laterTime;
      }
    );
    const roomTypeList = [
      { label: "Free Speech Room", value: "FreeToSpeak" },
      { label: "On-stage Speaking Room", value: "SpeakAfterTakingSeat" }
    ];
    const selectScheduleAttends = () => {
      contactsVisible.value = true;
    };
    const searchScheduleAttend = (v) => {
      if (!v) return [];
      return contacts.value.filter(
        (user) => (user == null ? void 0 : user.profile.nick.includes(v)) || (user == null ? void 0 : user.userID.includes(v))
      );
    };
    const addSelectUser = (user) => {
      var _a, _b;
      form.value.searchUser = "";
      if (form.value.scheduleAttendees.findIndex(
        (item) => item.userId === user.userID
      ) !== -1)
        return;
      form.value.scheduleAttendees.push({
        userId: user.userID,
        userName: (_a = user.profile) == null ? void 0 : _a.nick,
        avatarUrl: (_b = user.profile) == null ? void 0 : _b.avatar
      });
    };
    const removeSelectUser = (user) => {
      form.value.scheduleAttendees = form.value.scheduleAttendees.filter(
        (item) => item.userId !== user.userId
      );
    };
    const contactsConfirm = (contacts2) => {
      form.value.scheduleAttendees = contacts2;
    };
    let scheduleConferenceInProgress = false;
    const scheduleConference = async () => {
      if (scheduleConferenceInProgress) return;
      if (!timeCheck()) return;
      if (!roomNameCheck()) return;
      if (!roomPasswordCheck()) return;
      scheduleConferenceInProgress = true;
      try {
        roomId.value = await roomService.scheduleConferenceManager.generateRoomId();
        await roomService.scheduleConferenceManager.scheduleConference({
          ...scheduleParams.value,
          roomId: roomId.value
        });
        const userIdList = scheduleParams.value.scheduleAttendees.map(
          (item) => item.userId
        );
        if (userIdList && userIdList.length > 0) {
          await roomService.scheduleConferenceManager.addAttendeesByAdmin({
            roomId: roomId.value,
            userIdList
          });
        }
        shareLinkData.value = deepClone(scheduleParams.value);
        updateDialogVisible(false);
        showRoomInvite.value = true;
      } catch (err) {
        roomService.emit(EventType.ROOM_NOTICE_MESSAGE, {
          type: "error",
          message: err.message
        });
      }
      scheduleConferenceInProgress = false;
    };
    const compareArrays = (oldArray, newArray, key) => {
      const added = [];
      const removed = [];
      const newKeySet = new Set(newArray.map((item) => item[key]));
      const oldKeySet = new Set(oldArray.map((item) => item[key]));
      newArray.forEach((item) => {
        if (!oldKeySet.has(item[key])) {
          added.push(item);
        }
      });
      oldArray.forEach((item) => {
        if (!newKeySet.has(item[key])) {
          removed.push(item);
        }
      });
      return {
        added,
        removed
      };
    };
    const updateConferenceInfo = async () => {
      var _a;
      if (!roomStatusCheck()) return;
      if (!timeCheck()) return;
      if (!roomNameCheck()) return;
      try {
        const roomId2 = (_a = props.conferenceInfo) == null ? void 0 : _a.basicRoomInfo.roomId;
        if (!roomId2) return;
        const { roomName, scheduleStartTime, scheduleEndTime, scheduleAttendees } = scheduleParams.value;
        await roomService.scheduleConferenceManager.updateConferenceInfo({
          roomId: roomId2,
          roomName,
          scheduleStartTime,
          scheduleEndTime
        });
        const compareResult = compareArrays(
          props.conferenceInfo.scheduleAttendees,
          scheduleAttendees,
          "userId"
        );
        await Promise.all([
          compareResult.added.length > 0 && roomService.scheduleConferenceManager.addAttendeesByAdmin({
            roomId: roomId2,
            userIdList: compareResult.added.map((item) => item.userId)
          }),
          compareResult.removed.length > 0 && roomService.scheduleConferenceManager.removeAttendeesByAdmin({
            roomId: roomId2,
            userIdList: compareResult.removed.map((item) => item.userId)
          })
        ]);
        updateDialogVisible(false);
      } catch (err) {
        roomService.emit(EventType.ROOM_NOTICE_MESSAGE, {
          type: "error",
          message: err.message
        });
      }
    };
    const cancel = () => {
      updateDialogVisible(false);
    };
    return { __sfc: true, t, shareLinkData, props, emit, isDialogVisible, contactsVisible, showRoomInvite, isShowPassword, passwordChecked, isEditMode, roomId, contacts, startDate, startTime, defaultFormData, form, inputType, toggleShowPassword, resetData, updateDialogVisible, editParams, scheduleParams, roomPasswordCheck, timeCheck, roomNameCheck, roomStatusCheck, roomTypeList, selectScheduleAttends, searchScheduleAttend, addSelectUser, removeSelectUser, contactsConfirm, scheduleConferenceInProgress, scheduleConference, compareArrays, updateConferenceInfo, cancel, TuiInput: Input, TuiSelect: Select, TuiOption: OPtion, TuiCheckbox: Checkbox, TuiButton, TuiAvatar: Avatar, TuiDatepicker: Datepicker, TuiTimepicker: Timepicker, TuiDurationTimePicker, TimezonePicker, TuiShareLink, Contacts, ScheduleAttendees, PanelContainer, CloseIcon, HideContentIcon, ShowContentIcon, SvgIcon };
  }
});
export {
  _sfc_main as default
};
